import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { serverURL } from '../../constants';
import { authHeaders, getSessionAuth } from '../auth/session';
import { isLikelyPlatformTenantHost, platformTenantSlug } from './tenantHost';

const OrganizationContext = createContext({
    organization: null,
    branding: null,
    loading: true,
    error: null,
    refresh: () => {},
    applyLocalBranding: () => {},
});

const BRANDING_CACHE_PREFIX = 'coursolo:branding:v1:';

export function applyBranding(branding, name) {
    if (!branding) return;
    const root = document.documentElement;
    if (branding.primaryColor) {
        root.style.setProperty('--org-primary', branding.primaryColor);
    } else {
        root.style.setProperty('--org-primary', '#2563eb');
    }
    if (branding.secondaryColor) {
        root.style.setProperty('--org-secondary', branding.secondaryColor);
    } else {
        root.style.setProperty('--org-secondary', '#0f172a');
    }
    const titleName = branding.companyName || name;
    if (titleName) {
        document.title = titleName;
    }
    if (branding.faviconUrl) {
        let link = document.querySelector("link[rel*='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.head.appendChild(link);
        }
        link.href = branding.faviconUrl;
    }
}

function normalizeBranding(b = {}, name = '') {
    return {
        companyName: b.companyName || name || '',
        logoUrl: b.logoUrl || '',
        logoDarkUrl: b.logoDarkUrl || '',
        faviconUrl: b.faviconUrl || '',
        primaryColor: b.primaryColor || '',
        secondaryColor: b.secondaryColor || '',
        supportEmail: b.supportEmail || '',
        tagline: b.tagline || '',
        welcomeMessage: b.welcomeMessage || '',
    };
}

/** Prefer non-empty overlay fields so '' does not wipe host branding. */
function mergeBranding(base, overlay) {
    const out = { ...(base || {}) };
    Object.entries(overlay || {}).forEach(([key, value]) => {
        if (value == null) return;
        if (typeof value === 'string' && !value.trim()) return;
        out[key] = value;
    });
    return out;
}

function hasVisualChrome(b) {
    return Boolean(
        b?.logoUrl
        || b?.logoDarkUrl
        || b?.faviconUrl
        || b?.primaryColor
        || b?.secondaryColor
    );
}

function brandingCacheKey() {
    try {
        const host = window.location.host;
        const { organizationId } = getSessionAuth();
        if (organizationId) return `${BRANDING_CACHE_PREFIX}org:${organizationId}`;
        return `${BRANDING_CACHE_PREFIX}host:${host}`;
    } catch {
        return `${BRANDING_CACHE_PREFIX}host:unknown`;
    }
}

function readBrandingCache() {
    try {
        const keys = [brandingCacheKey()];
        // Also try host key when session org key misses (first paint before claims)
        try {
            keys.push(`${BRANDING_CACHE_PREFIX}host:${window.location.host}`);
            const oid = sessionStorage.getItem('organizationId');
            if (oid) keys.unshift(`${BRANDING_CACHE_PREFIX}org:${oid}`);
        } catch { /* ignore */ }

        for (const key of keys) {
            const raw = localStorage.getItem(key);
            if (!raw) continue;
            const parsed = JSON.parse(raw);
            if (!parsed?.branding) continue;
            return {
                branding: normalizeBranding(parsed.branding, parsed.organization?.name),
                organization: parsed.organization || null,
            };
        }
    } catch {
        /* ignore corrupt cache */
    }
    return null;
}

function writeBrandingCache(branding, organization) {
    if (!branding || !hasVisualChrome(branding)) {
        // Still cache name-only branding for title consistency when tenant
        if (!branding?.companyName && !organization?.name) return;
    }
    try {
        const payload = JSON.stringify({
            branding: normalizeBranding(branding, organization?.name),
            organization: organization
                ? {
                    id: organization.id || null,
                    slug: organization.slug || null,
                    name: organization.name || branding?.companyName || '',
                    tenant: Boolean(organization.tenant),
                    plan: organization.plan || null,
                }
                : null,
            updatedAt: Date.now(),
        });
        const hostKey = `${BRANDING_CACHE_PREFIX}host:${window.location.host}`;
        localStorage.setItem(hostKey, payload);
        const oid = organization?.id || sessionStorage.getItem('organizationId');
        if (oid) {
            localStorage.setItem(`${BRANDING_CACHE_PREFIX}org:${oid}`, payload);
        }
    } catch {
        /* quota / private mode */
    }
}

function readInitialBrandingState() {
    const cached = readBrandingCache();
    if (!cached?.branding) {
        return { branding: null, organization: null, hydrated: false };
    }
    applyBranding(cached.branding, cached.organization?.name);
    return {
        branding: cached.branding,
        organization: cached.organization,
        hydrated: true,
    };
}

export function OrganizationProvider({ children }) {
    const [initial] = useState(readInitialBrandingState);
    const [organization, setOrganization] = useState(initial.organization);
    const [branding, setBranding] = useState(initial.branding);
    const [loading, setLoading] = useState(!initial.hydrated);
    const [error, setError] = useState(null);

    const applyLocalBranding = useCallback((nextBranding, orgMeta = {}) => {
        const normalized = normalizeBranding(nextBranding, orgMeta.name || organization?.name);
        const nextOrg = {
            ...(organization || {}),
            id: orgMeta.id || orgMeta.organizationId || organization?.id,
            slug: orgMeta.slug || organization?.slug,
            name: orgMeta.name || organization?.name || normalized.companyName,
            tenant: Boolean(organization?.tenant),
        };
        setBranding(normalized);
        applyBranding(normalized, nextOrg.name);
        writeBrandingCache(normalized, nextOrg);
        if (orgMeta.slug || orgMeta.id || orgMeta.organizationId) {
            setOrganization(nextOrg);
        }
    }, [organization]);

    const refresh = useCallback(async () => {
        try {
            // Keep cached chrome visible while revalidating — avoid flashing defaults
            if (!readBrandingCache()?.branding) setLoading(true);
            setError(null);
            const host = window.location.host;
            const hostname = window.location.hostname;
            const likelyTenant = isLikelyPlatformTenantHost(hostname);
            const { token } = getSessionAuth();

            const publicPromise = axios.get(`${serverURL}/api/public/branding`, {
                params: { host },
                timeout: 10000,
            });
            const mePromise = token
                ? axios.get(`${serverURL}/api/org/me`, {
                    headers: authHeaders(),
                    timeout: 10000,
                }).catch(() => null)
                : Promise.resolve(null);

            const [{ data }, meRes] = await Promise.all([publicPromise, mePromise]);

            let nextOrg = null;
            let nextBranding = null;
            const hostIsTenant = Boolean(data?.tenant && data?.organizationId);

            if (data?.success) {
                if (hostIsTenant) {
                    nextOrg = {
                        id: data.organizationId,
                        slug: data.slug,
                        name: data.name,
                        plan: data.plan,
                        features: data.features,
                        settings: data.settings,
                        sso: data.sso || {},
                        tenant: true,
                    };
                    nextBranding = data.branding ? normalizeBranding(data.branding, data.name) : null;
                } else if (likelyTenant) {
                    const slug = platformTenantSlug(hostname);
                    nextOrg = {
                        id: null,
                        slug,
                        name: slug || '',
                        tenant: true,
                        features: {},
                        settings: {},
                        sso: {},
                    };
                } else if (data.branding) {
                    nextBranding = normalizeBranding(data.branding, data.branding.companyName);
                }
            }

            const me = meRes?.data;
            if (me?.success && me.organization) {
                const org = me.organization;
                const sessionBranding = normalizeBranding(
                    {
                        ...(org.branding || {}),
                        supportEmail: org.contact?.supportEmail || '',
                    },
                    org.name
                );
                const sameOrg = !hostIsTenant || String(nextOrg?.id) === String(org._id);
                if (sameOrg) {
                    nextOrg = {
                        id: org._id,
                        slug: org.slug,
                        name: org.name,
                        plan: org.plan,
                        features: org.features || nextOrg?.features || {},
                        settings: org.settings || nextOrg?.settings || {},
                        sso: org.sso || nextOrg?.sso || {},
                        tenant: hostIsTenant,
                    };
                    if (hasVisualChrome(sessionBranding) || hostIsTenant) {
                        nextBranding = mergeBranding(nextBranding, sessionBranding);
                        if (!nextBranding.companyName) {
                            nextBranding.companyName = sessionBranding.companyName || org.name;
                        }
                    } else if (!nextBranding?.companyName && (sessionBranding.companyName || org.name)) {
                        nextBranding = mergeBranding(nextBranding || normalizeBranding({}, org.name), {
                            companyName: sessionBranding.companyName || org.name,
                        });
                    }
                }
            }

            if (!nextOrg && likelyTenant) {
                const slug = platformTenantSlug(hostname);
                nextOrg = {
                    id: null,
                    slug,
                    name: slug || '',
                    tenant: true,
                    features: {},
                    settings: {},
                    sso: {},
                };
            }

            setOrganization(nextOrg);
            setBranding(nextBranding);
            if (nextBranding) {
                applyBranding(nextBranding, nextOrg?.name);
                writeBrandingCache(nextBranding, nextOrg);
            }
        } catch (err) {
            const hostname = window.location.hostname;
            if (isLikelyPlatformTenantHost(hostname)) {
                const slug = platformTenantSlug(hostname);
                setOrganization((prev) => prev || {
                    id: null,
                    slug,
                    name: slug || '',
                    tenant: true,
                    features: {},
                    settings: {},
                    sso: {},
                });
                setError(err?.message || 'Failed to load workspace branding');
            }
            // Keep cached branding on network failure — do not clear to defaults
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    useEffect(() => {
        const onStorage = (e) => {
            if (!e.key || e.key === 'token' || e.key === 'organizationId' || e.key === 'orgRole') {
                refresh();
            }
        };
        window.addEventListener('storage', onStorage);
        const onAuthClaims = () => refresh();
        window.addEventListener('coursolo:auth-claims', onAuthClaims);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('coursolo:auth-claims', onAuthClaims);
        };
    }, [refresh]);

    return (
        <OrganizationContext.Provider value={{ organization, branding, loading, error, refresh, applyLocalBranding }}>
            {children}
        </OrganizationContext.Provider>
    );
}

export function useOrganization() {
    return useContext(OrganizationContext);
}

export default OrganizationContext;
